import React from 'react';
import { Link } from 'react-router-dom';
import { PageHero, CTASection } from '@/components/shared/Section';
import { fetchBlogPosts } from '@/lib/api';
import { ArrowUpRight, Calendar, Clock } from 'lucide-react';
import { BLOG } from '@/constants/testIds';

export default function BlogPage() {
  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetchBlogPosts()
      .then(setPosts)
      .finally(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <>
      <PageHero
        eyebrow="Insights"
        title="Ideas, frameworks and stories from the world of business growth."
        description="Practical writing on recruitment, leadership, workforce technology and SME development — for operators who think long-term."
      />

      <section data-testid={BLOG.list} className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
        {loading ? (
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 h-96 bg-slate-100 rounded-sm animate-pulse" />
            <div className="lg:col-span-5 grid gap-4">
              {[1,2,3].map(i => <div key={i} className="h-28 bg-slate-100 rounded-sm animate-pulse" />)}
            </div>
          </div>
        ) : (
          <>
            {featured && (
              <Link
                to={`/insights/${featured.slug}`}
                data-testid={BLOG.card}
                className="group grid lg:grid-cols-12 gap-8 mb-16"
              >
                <div className="lg:col-span-7 relative overflow-hidden rounded-sm border border-slate-200">
                  <img src={featured.cover_image} alt={featured.title} className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="lg:col-span-5 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 self-start">
                    <span className="px-2.5 py-1 rounded-sm bg-tito-gold/15 text-[10px] uppercase tracking-[0.18em] font-bold text-[#003B73]">
                      Featured
                    </span>
                    <span className="text-xs text-slate-500">{featured.category}</span>
                  </div>
                  <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 group-hover:text-tito-blue transition-colors">
                    {featured.title}
                  </h2>
                  <p className="mt-4 text-slate-600 leading-relaxed">{featured.excerpt}</p>
                  <div className="mt-6 flex items-center gap-5 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{featured.published_at}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{featured.read_time}</span>
                  </div>
                  <div className="mt-6 inline-flex items-center gap-1.5 font-medium text-tito-blue">
                    Read article <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((p) => (
                <Link
                  key={p.id}
                  to={`/insights/${p.slug}`}
                  data-testid={BLOG.card}
                  className="group flex flex-col rounded-sm border border-slate-200 bg-white overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.cover_image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-tito-blue/70">{p.category}</div>
                    <h3 className="mt-3 font-display font-semibold text-xl text-slate-900 group-hover:text-tito-blue transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <div className="mt-5 pt-5 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                      <span>{p.published_at}</span>
                      <span className="inline-flex items-center gap-1 text-tito-blue font-medium" data-testid={BLOG.readMore}>
                        Read <ArrowUpRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </section>

      <CTASection />
    </>
  );
}
